<template>
  <v-content class="download-page">
    <v-container fluid class="fill-height pa-0">
      <v-row no-gutters class="fill-height">
        <!-- Left section -->
        <v-col cols="12" md="6" class="download-left d-flex align-center justify-center">
          <div class="download-left-content text-center">
            <img :src="require('@/assets/logo_new.svg')" alt="Kiey" class="download-logo mb-6" />
            <h1 class="download-title">Kiey for Desktop</h1>
            <p class="download-subtitle">
              Fast, secure team communication right from your desktop.
              All your chats, files, and invoices in one place.
            </p>
          </div>
        </v-col>

        <!-- Right section -->
        <v-col cols="12" md="6" class="download-right d-flex align-center justify-center">
          <div class="download-right-content">
            <!-- Primary download button (auto-detected OS) -->
            <div class="primary-download text-center mb-8">
              <v-btn
                x-large
                rounded
                color="#061D47"
                dark
                class="download-btn-primary"
                :href="primaryDownload.url"
                :disabled="!primaryDownload.url"
              >
                <v-icon left>{{ primaryDownload.icon }}</v-icon>
                Download for {{ primaryDownload.label }}
              </v-btn>
              <p class="download-version mt-3">Version {{ appVersion }} &middot; {{ primaryDownload.size }}</p>
            </div>

            <v-divider class="mb-6"></v-divider>

            <!-- All platforms -->
            <h3 class="platforms-title mb-4">All Platforms</h3>
            <div class="platform-list">
              <div
                v-for="(platform, index) in platforms"
                :key="platform.os"
                class="platform-item d-flex align-center justify-space-between pa-4 mb-3"
                :class="{ 'platform-item--recommended': platform.os === detectedOS }"
                :style="{ '--stagger': index }"
              >
                <div class="d-flex align-center">
                  <v-icon class="mr-3" color="#061D47">{{ platform.icon }}</v-icon>
                  <div>
                    <div class="platform-name">
                      {{ platform.label }}
                      <span
                        v-if="platform.os === detectedOS"
                        class="recommended-badge"
                      >Recommended</span>
                    </div>
                    <div class="platform-detail">{{ platform.detail }}</div>
                  </div>
                </div>
                <v-btn
                  outlined
                  small
                  color="#061D47"
                  :href="platform.url"
                  :disabled="!platform.url"
                >
                  {{ platform.url ? 'Download' : 'Coming Soon' }}
                </v-btn>
              </div>
            </div>

            <!-- Windows SmartScreen guidance -->
            <v-alert
              v-if="isWindows"
              text
              color="#061D47"
              class="windows-note mt-4"
              icon="fab fa-windows"
            >
              <div class="windows-note-title">Windows will show two warnings &mdash; this is normal</div>
              <p class="mb-2">
                Kiey is a new app, so Windows hasn't built up a reputation for it yet.
                You may see one or both of these safety prompts &mdash; here's how to get past them:
              </p>
              <ol class="windows-note-steps mb-2">
                <li>
                  <strong>Browser warning</strong> (if it appears)
                  <span class="windows-note-step-detail">
                    &ldquo;Kiey-latest-setup.exe isn't commonly downloaded.&rdquo;
                    Open your browser's downloads list and click into the warning &mdash;
                    look for <strong>See more</strong>, the <strong>&hellip;</strong> menu, or the
                    <strong>&#8942;</strong> menu &mdash; then choose <strong>Keep</strong>.
                  </span>
                </li>
                <li>
                  <strong>SmartScreen popup</strong> when you open the installer
                  <span class="windows-note-step-detail">
                    &ldquo;Windows protected your PC.&rdquo; You'll only see a <em>Don't run</em> button.
                    Click <strong>More info</strong> at the top of the popup &mdash; that reveals the
                    <strong>Run anyway</strong> button.
                  </span>
                </li>
              </ol>
              <p class="windows-note-fallback mb-0">
                Still stuck? Use the <strong>Windows (Portable)</strong> download instead &mdash; unzip and
                double-click <code>Kiey.exe</code>. The same warnings can appear on first run;
                click <strong>More info</strong> &rarr; <strong>Run anyway</strong> the same way.
              </p>
            </v-alert>

            <!-- System requirements -->
            <div class="system-req mt-6">
              <h4 class="mb-2">System Requirements</h4>
              <ul>
                <li>macOS 10.15 (Catalina) or later</li>
                <li>Windows 10 or later (64-bit)</li>
                <li>Ubuntu 18.04+ / Fedora 32+ / Debian 10+</li>
              </ul>
            </div>

            <!-- Links -->
            <div class="download-links mt-6 text-center">
              <router-link to="/" class="download-link">Sign in to Kiey Web</router-link>
              <span class="mx-2">|</span>
              <a href="https://kiey.com/privacy-policy.html" target="_blank" rel="noopener" class="download-link">Privacy Policy</a>
              <span class="mx-2">|</span>
              <a href="https://kiey.com/terms.html" target="_blank" rel="noopener" class="download-link">Terms</a>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-content>
</template>

<script>
const DOWNLOAD_BASE = 'https://storage.googleapis.com/kiey-production.firebasestorage.app/desktop-releases';
const APP_VERSION = '1.0.0';

export default {
  name: 'DownloadDesktop',
  data() {
    return {
      appVersion: APP_VERSION,
      platforms: [
        {
          os: 'mac',
          label: 'macOS',
          detail: 'Apple Silicon & Intel',
          icon: 'fab fa-apple',
          size: '~150 MB',
          url: `${DOWNLOAD_BASE}/Kiey-latest.dmg`,
        },
        {
          os: 'win',
          label: 'Windows (Installer)',
          detail: '64-bit (Windows 10+)',
          icon: 'fab fa-windows',
          size: '~130 MB',
          url: `${DOWNLOAD_BASE}/Kiey-latest-setup.exe`,
        },
        {
          os: 'win-portable',
          label: 'Windows (Portable)',
          detail: 'No install — unzip and run Kiey.exe',
          icon: 'fab fa-windows',
          size: '~140 MB',
          url: `${DOWNLOAD_BASE}/Kiey-latest-win.zip`,
        },
        {
          os: 'linux',
          label: 'Linux',
          detail: 'AppImage (Ubuntu, Fedora, Debian)',
          icon: 'fab fa-linux',
          size: '~140 MB',
          url: `${DOWNLOAD_BASE}/Kiey-latest.AppImage`,
        },
      ],
    };
  },
  computed: {
    detectedOS() {
      const ua = navigator.userAgent.toLowerCase();
      if (ua.includes('mac')) return 'mac';
      if (ua.includes('win')) return 'win';
      if (ua.includes('linux')) return 'linux';
      return 'mac';
    },
    isWindows() {
      return this.detectedOS === 'win';
    },
    primaryDownload() {
      return this.platforms.find(p => p.os === this.detectedOS) || this.platforms[0];
    },
  },
};
</script>

<style scoped lang="scss">
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

@keyframes gradientShift {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes pulseGlow {
  0%, 100% { box-shadow: 0 8px 24px rgba(6, 29, 71, 0.25); }
  50%      { box-shadow: 0 12px 32px rgba(6, 29, 71, 0.4); }
}

.download-page {
  background: #fff;
  min-height: 100vh;
}

.download-left {
  background: linear-gradient(135deg, #061D47 0%, #0d3a8f 50%, #061D47 100%);
  background-size: 200% 200%;
  animation: gradientShift 18s ease infinite;
  padding: 60px 40px;
}

.download-left-content {
  max-width: 420px;
  animation: fadeInUp 0.7s ease both;
}

.download-logo {
  max-width: 180px;
  filter: brightness(0) invert(1);
}

.download-title {
  font-size: 36px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 16px;
  letter-spacing: -0.5px;
}

.download-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
}

.download-right {
  padding: 60px 40px;
}

.download-right-content {
  max-width: 480px;
  width: 100%;
  animation: fadeInUp 0.7s ease 0.15s both;
}

.download-btn-primary {
  font-size: 16px !important;
  letter-spacing: 0.3px;
  padding: 12px 40px !important;
  height: 60px !important;
  min-width: 280px !important;
  animation: pulseGlow 3s ease-in-out infinite;
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 36px rgba(6, 29, 71, 0.45) !important;
    animation: none;
  }

  &:active {
    transform: translateY(0);
  }
}

.download-version {
  font-size: 13px;
  color: #888;
}

.platforms-title {
  font-size: 16px;
  font-weight: 600;
  color: #061D47;
}

.platform-item {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fff;
  transition: border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
  animation: fadeInUp 0.5s ease both;
  animation-delay: calc(var(--stagger, 0) * 60ms + 0.3s);

  &:hover {
    border-color: #061D47;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(6, 29, 71, 0.1);
  }
}

.platform-item--recommended {
  border-color: #061D47;
  background: linear-gradient(135deg, rgba(6, 29, 71, 0.04) 0%, rgba(13, 58, 143, 0.06) 100%);
  box-shadow: 0 4px 16px rgba(6, 29, 71, 0.08);
}

.recommended-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #fff;
  background: #061D47;
  border-radius: 10px;
  vertical-align: middle;
}

.platform-name {
  font-size: 15px;
  font-weight: 600;
  color: #061D47;
}

.platform-detail {
  font-size: 12px;
  color: #888;
}

.system-req {
  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #061D47;
  }

  ul {
    list-style: none;
    padding-left: 0;

    li {
      font-size: 13px;
      color: #666;
      padding: 2px 0;

      &::before {
        content: '\2022';
        color: #061D47;
        font-weight: bold;
        margin-right: 8px;
      }
    }
  }
}

.windows-note {
  font-size: 13px;
  line-height: 1.5;

  code {
    background: rgba(6, 29, 71, 0.08);
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 12px;
  }
}

.windows-note-title {
  font-weight: 700;
  color: #061D47;
  font-size: 14px;
  margin-bottom: 6px;
}

.windows-note-steps {
  padding-left: 20px;

  li {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.windows-note-step-detail {
  display: block;
  margin-top: 2px;
  color: #5a5a60;
}

.windows-note-fallback {
  padding-top: 10px;
  border-top: 1px solid rgba(6, 29, 71, 0.15);
}

.download-links {
  font-size: 13px;
}

.download-link {
  color: #061D47;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

@media (max-width: 960px) {
  .download-left {
    padding: 56px 24px 48px;
    min-height: auto;
  }

  .download-left-content {
    max-width: 100%;
  }

  .download-logo {
    max-width: 140px;
  }

  .download-title {
    font-size: 30px;
    margin-bottom: 12px;
  }

  .download-subtitle {
    font-size: 15px;
    max-width: 480px;
    margin: 0 auto;
  }

  .download-right {
    padding: 40px 20px 56px;
  }

  .download-right-content {
    max-width: 100%;
  }

  .download-btn-primary {
    width: 100%;
    min-width: 0 !important;
    padding: 12px 24px !important;
  }

  .platform-item {
    padding: 16px !important;
  }

  .platform-name {
    font-size: 14px;
  }

  .recommended-badge {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 6px;
    font-size: 9px;
  }
}

@media (max-width: 480px) {
  .download-title {
    font-size: 26px;
  }

  .download-subtitle {
    font-size: 14px;
  }

  .platform-item {
    flex-wrap: wrap;
    gap: 12px;

    .v-btn {
      width: 100%;
    }
  }

  .download-links {
    line-height: 2;

    .mx-2 {
      display: none;
    }

    .download-link {
      display: inline-block;
      padding: 0 8px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .download-left,
  .download-left-content,
  .download-right-content,
  .download-btn-primary,
  .platform-item {
    animation: none !important;
  }

  .download-btn-primary:hover,
  .platform-item:hover {
    transform: none;
  }
}
</style>
